/**
 * dashboards.ts — общие части дашбордов кабинета нового вида (WEB-3748 юнит 2):
 * тексты окна настройки блока `Pick` и названия с подсказками метрик каталога
 * `components/dashboards/metricCatalog.ts`; юнит 3 — график по дням (выбор
 * метрики, заголовок, легенда) и пункты графиков в настройке раскладки; доводка —
 * плашка «Баланс пуст» (`BalanceEmptyNotice`, тексты `BalEmpty` эталона). Берут
 * главная проекта (WEB-3748) и дашборд организации (WEB-3759).
 *
 * Тексты — из `TX.cf` и `DM` эталона `design/cabinet-rescin/sections/home-x.jsx`.
 * Отличия по коду (правило владельца 23.10: данные — по коду):
 *  - подсказка «Покупки» — по смыслу поля сервера `purchases` (успешные
 *    оформления оплаты в воронках, `analytics-response.dto.ts`), а не «первые
 *    оплаты без продлений» эталона;
 *  - подсказки «Выручки» здесь нет: она из кода, `revenueHint` в
 *    `utils/revenueMetric.ts` (ответ reskin 8).
 */
import { defineDictionary } from '../define';
import { analyticsStrings } from './analytics';

export const dashboardsStrings = defineDictionary(
  {
    cfgBlock: 'Customize block',
    cfgShown: 'Shown',
    cfgAdd: 'Add',
    cfgReset: 'Reset',
    cfgUp: 'Up',
    cfgDown: 'Down',
    cfgMin: (n: number) =>
      n === 1 ? 'At least one is needed' : `At least ${n} are needed`,
    cfgMax: (n: number) => `No more than ${n}`,
    cfgChart: (m: string) => `Chart: ${m}`,
    cfgMaxCharts: (n: number) => `No more than ${n} charts — remove one`,

    chartKind: 'On the chart',
    chartByDay: (m: string) => `${m} by day`,
    chartByHour: (m: string) => `${m} by hour`,
    legCur: 'This period',
    legPrev: 'Previous period',

    metricVis: 'Visitors',
    metricVisTip: 'Uniques: one person in two tabs counts once',
    metricBuy: 'Purchases',
    metricBuyTip: 'Successful checkouts in the funnels during the period',
    metricCr: 'Purchase conversion',
    metricCrTip: 'Purchases / visitors',
    metricRev: 'Revenue',
    metricArpu: 'Revenue per visitor',
    metricArpuTip: 'Revenue / visitors — compare with your cost per click',
    metricArppu: 'Revenue per payer',
    metricArppuTip: 'Revenue / purchases',
    metricPwBuy: 'Paywall to purchase',
    metricPwBuyTip: 'Purchases / saw the paywall',
    metricRefund: 'Refund rate',
    // WEB-3740 юнит 53-1: одна подсказка на все места — текст аналитики
    metricRefundTip: analyticsStrings.en.metRefundRateH,
    metricTrialPaid: 'Trial to paid',
    metricTrialPaidTip: 'Share of trials that converted to paid',
    metricNa: 'unavailable',
    metricFailed: 'Could not load',

    balEmptyTitle: 'The balance is empty',
    balEmptyText:
      'AI generation and domain renewals are paused. Funnels and payments keep working.',
    balEmptyTopUp: 'Top up',
  },
  {
    cfgBlock: 'Настроить блок',
    cfgShown: 'Показаны',
    cfgAdd: 'Добавить',
    cfgReset: 'Как было',
    cfgUp: 'Выше',
    cfgDown: 'Ниже',
    cfgMin: (n: number) =>
      n === 1 ? 'Нужен хотя бы один' : `Нужно хотя бы ${n}`,
    cfgMax: (n: number) => `Не больше ${n}`,
    cfgChart: (m: string) => `График: ${m.toLowerCase()}`,
    cfgMaxCharts: (n: number) => `Графиков не больше ${n} — уберите один`,

    chartKind: 'На графике',
    chartByDay: (m: string) => `${m} по дням`,
    chartByHour: (m: string) => `${m} по часам`,
    legCur: 'Этот период',
    legPrev: 'Прошлый период',

    metricVis: 'Посетители',
    metricVisTip: 'Уники: один человек в двух вкладках — один',
    metricBuy: 'Покупки',
    metricBuyTip: 'Успешные оформления оплаты в воронках за период',
    metricCr: 'Конверсия в покупку',
    metricCrTip: 'Покупки / посетители',
    metricRev: 'Выручка',
    metricArpu: 'Доход с посетителя',
    metricArpuTip: 'Выручка / посетители — сравнивайте с ценой клика',
    metricArppu: 'Доход с платящего',
    metricArppuTip: 'Выручка / покупки',
    metricPwBuy: 'Из пейволла в покупку',
    metricPwBuyTip: 'Покупки / увидели пейволл',
    metricRefund: 'Доля возвратов',
    metricRefundTip: analyticsStrings.ru.metRefundRateH,
    metricTrialPaid: 'Из пробного в платный',
    metricTrialPaidTip: 'Доля пробных периодов, перешедших в оплату',
    metricNa: 'недоступно',
    metricFailed: 'Не удалось загрузить',

    balEmptyTitle: 'Баланс пуст',
    balEmptyText:
      'ИИ-генерации и продление доменов на паузе. Воронки и оплаты работают.',
    balEmptyTopUp: 'Пополнить',
  },
);
